import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend,
} from 'recharts';
import { ArrowLeft, Phone } from 'lucide-react';
import tokens from '../design/tokens';
import InstrumentChart from './InstrumentChart';
import {
  getMotorReadings, actionForGrade,
  getSizeClass, ISO_THRESHOLDS, gradeFromVelocity, tileForGrade,
  timeAgo, isOffline, cToF, offlineLabel, FADED_OPACITY,
} from '../services/monitoringApi';

// Same visual system as the Fleet cards: ivory ground, white cards, navy ink,
// IBM Plex Mono for every reading. Inline tokens, not utility classes — the
// legacy .dark-theme overrides in index.css repaint bg-white/text-slate-*.
const C = tokens.color;
const F = tokens.font;
const R = tokens.radius;
const G = tokens.grade;
const MONO = { fontFamily: F.mono };
const DISP = { fontFamily: F.display };
const CARD = { background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R.xl, padding: 20, marginBottom: 16 };
const H2 = { ...DISP, fontWeight: 700, fontSize: 18, color: C.ink, margin: '0 0 14px' };
const TILE_LABEL = { good: 'Good', watch: 'Watch', attention: 'Attention', critical: 'Critical' };

/** Grade chip + word, the same letter and colours the Fleet card shows.
 *  Offline (D2 #10): the LAST grade, faded, then "Offline · last reading <age>". */
export function StatusBadge({ grade, offline, lastTimestamp }) {
  const chip = grade ? (
    <span data-testid="grade-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: R.full, background: G[grade].bg, color: G[grade].fg, fontWeight: 600, fontSize: 13, opacity: offline ? FADED_OPACITY : 1 }}>
      <span style={{ ...DISP, fontWeight: 800 }}>{grade}</span> · {TILE_LABEL[tileForGrade(grade)]}
    </span>
  ) : null;
  if (!offline) return chip;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {chip}
      <span style={{ ...MONO, fontSize: 12, padding: '6px 12px', borderRadius: R.full, background: C.surfaceAlt, color: C.textMuted }}>{offlineLabel(lastTimestamp)}</span>
    </span>
  );
}

/**
 * Y domain centred on the data, never pinned to 0, and at least `minSpan` wide —
 * so a normal few-degree daily swing reads as flat, not as spikes. A wider data
 * range gets 15% headroom each side instead. Ends snap to `step` so the ticks
 * are even (141/151/… read as noise; 140/150/… don't).
 */
export function centredDomain(values, minSpan, step = 10) {
  const v = values.filter((x) => Number.isFinite(x));
  if (!v.length) return { domain: ['auto', 'auto'], ticks: undefined };
  const lo = Math.min(...v), hi = Math.max(...v);
  const span = Math.max(minSpan, (hi - lo) * 1.3);
  const mid = (lo + hi) / 2;
  const a = Math.floor((mid - span / 2) / step) * step;
  // Build the span up from the snapped bottom (not both ends rounded outward,
  // which can add a whole step on top and push the data low).
  const raw = Math.max(a + Math.ceil(span / step) * step, Math.ceil(hi / step) * step);
  const every = step * Math.ceil((raw - a) / step / 6);   // at most 7 ticks
  const b = a + Math.ceil((raw - a) / every) * every;
  const ticks = [];
  for (let t = a; t <= b; t += every) ticks.push(t);
  return { domain: [a, b], ticks };
}

const AXIS_TICK = { fontSize: 11, fill: C.textMuted, fontFamily: F.mono };
const TOOLTIP = { contentStyle: { borderRadius: 10, border: `1px solid ${C.hairline}`, fontFamily: F.mono, fontSize: 12 } };

export default function MotorDetail({ motor, onBack, shopPhone }) {
  // 30 days by default: long enough that a trend (Mixer Drive's knee) shows on open.
  const [period, setPeriod] = useState('30d');
  const [readings, setReadings] = useState([]);
  const [loadingReadings, setLoadingReadings] = useState(true);

  const motorId = motor.motorId || motor.id;
  const hp = motor.hp || motor.motorHp;
  const thresholds = ISO_THRESHOLDS[getSizeClass(hp)];
  const velocity = motor.latestReading?.rmsVelocity;
  const offline = isOffline(motor.latestReading?.timestamp);
  // The last grade is kept for an offline motor (shown faded); only a
  // reporting motor's grade drives the analysis and the attention styling.
  const lastGrade = gradeFromVelocity(velocity, hp);
  const grade = offline ? null : lastGrade;
  const needsAttention = grade === 'C' || grade === 'D' || grade === 'F';

  useEffect(() => {
    let cancelled = false;
    setLoadingReadings(true);
    getMotorReadings(motorId, period)
      .then(res => { if (!cancelled) setReadings(res.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingReadings(false); });
    return () => { cancelled = true; };
  }, [motorId, period]);


  // Alert markers: where a reading crossed this motor's unsatisfactory
  // threshold — i.e. where the health check would have fired. It re-arms only
  // after the motor drops back below `satisfactory`, so a motor sitting on the
  // line (Mixer Drive over 30 days) shows one alert, not one per wobble.
  const alertMarkers = [];
  {
    let armed = true;
    for (const r of readings) {
      const v = r.rmsVelocity ?? 0;
      if (armed && v > thresholds.unsatisfactory) {
        alertMarkers.push({ timestamp: r.timestamp, label: 'Alert' });
        armed = false;
      } else if (!armed && v < thresholds.satisfactory) {
        armed = true;
      }
    }
  }

  // Chart data. 24h labels by time of day; 7d/30d by date.
  const fmtTime = (ts) => (period === '24h'
    ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' }));
  const chartData = readings.map(r => ({
    time: fmtTime(r.timestamp),
    // The API serves Celsius; this chart is in degF (see cToF in monitoringApi).
    temperature: cToF(r.temperature),
    peakX: r.peakX1,
    peakY: r.peakY1,
    peakZ: r.peakZ1,
  }));
  // Panels show only when their data exists — never an empty chart or a "—" tile.
  const hasTemp = chartData.some((d) => d.temperature != null);
  const hasAxes = chartData.some((d) => d.peakX != null || d.peakY != null || d.peakZ != null);
  const peakAccel = motor.latestReading?.peakX1;
  const tempAxis = centredDomain(chartData.map((d) => d.temperature), 40);

  const periods = [
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
  ];
  const vibColor = grade ? G[grade].fg : C.ink;
  const tiles = [
    { label: 'Vibration', value: velocity != null ? `${velocity.toFixed(2)} mm/s` : '—', color: vibColor },
    { label: 'Temperature', value: cToF(motor.latestReading?.temperature) != null ? `${cToF(motor.latestReading.temperature)}°F` : '—' },
    ...(peakAccel != null ? [{ label: 'Peak accel', value: `${peakAccel.toFixed(1)} g` }] : []),
    { label: 'Last reading', value: timeAgo(motor.latestReading?.timestamp), color: offline ? C.textMuted : undefined },
  ];

  return (
    <div className="bs-motor" style={{ maxWidth: 960, margin: '0 auto', padding: '24px 32px 40px', color: C.ink, fontFamily: F.body }}>
      <style>{`
        .bs-motor-tiles { display: grid; grid-template-columns: repeat(${tiles.length}, minmax(0,1fr)); gap: 12px; }
        @media (max-width: 640px) {
          .bs-motor { padding: 16px 16px 32px !important; }
          .bs-motor-tiles { grid-template-columns: repeat(2, minmax(0,1fr)); }
          .bs-motor-head { flex-direction: column; align-items: flex-start !important; }
          .bs-metric-value { font-size: 17px !important; white-space: normal !important; }
        }
      `}</style>

      {/* Top bar: back + call */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 0, padding: 0, cursor: 'pointer', color: C.link, fontWeight: 600, fontSize: 14, fontFamily: F.body }}>
          <ArrowLeft className="w-4 h-4" /> Back to Fleet
        </button>
        {needsAttention && (
          // Navy — the primary action style. Red is reserved for Critical status.
          <a href="tel:4237904745" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: R.md, background: C.ink, color: '#FFFFFF', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
            <Phone className="w-4 h-4" /> Call us · 423-790-4745
          </a>
        )}
      </div>

      {/* Motor header card */}
      <div style={{ ...CARD, padding: 24 }}>
        <div className="bs-motor-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ ...DISP, fontWeight: 800, fontSize: 26, margin: 0, color: C.ink }}>{motor.motorName || motor.name || 'Asset'}</h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: C.textMuted }}>
              {[motor.locationName || motor.location, hp ? `${hp} HP` : null, (motor.rpm || motor.motorRpm) ? `${motor.rpm || motor.motorRpm} RPM` : null].filter(Boolean).join(' · ')}
            </p>
          </div>
          <StatusBadge grade={lastGrade} offline={offline} lastTimestamp={motor.latestReading?.timestamp} />
        </div>
        <div className="bs-motor-tiles" style={{ marginTop: 20 }}>
          {tiles.map((t) => <MetricCard key={t.label} {...t} />)}
        </div>
      </div>

      {/* Period selector */}
      <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {periods.map(p => {
          const on = period === p.value;
          return (
            <button key={p.value} role="tab" aria-selected={on} onClick={() => setPeriod(p.value)}
                    style={{ padding: '8px 16px', borderRadius: R.md, fontSize: 14, fontWeight: 600, fontFamily: F.body, cursor: 'pointer',
                             background: on ? C.ink : C.surface, color: on ? '#FFFFFF' : C.ink,
                             border: `1px solid ${on ? C.ink : C.hairlineStrong}` }}>
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Vibration trend — the same instrument panel as the homepage hero,
          driven by this motor's real readings and its own ISO size class. */}
      <div style={CARD}>
        <h2 style={H2}>Velocity — {periods.find(p => p.value === period)?.label || period}</h2>
        {loadingReadings ? (
          <div className="skeleton" style={{ height: 300, borderRadius: 12 }} />
        ) : (
          <InstrumentChart readings={readings} hp={hp} alerts={alertMarkers} />
        )}
      </div>

      {/* Temperature trend — muted ink: temperature is not an ISO status reading. */}
      {!loadingReadings && hasTemp && (
        <div style={CARD}>
          <h2 style={H2}>Temperature</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 5, right: 8, bottom: 5, left: 0 }}>
              <CartesianGrid stroke={C.hairline} vertical={false} />
              <XAxis dataKey="time" tick={AXIS_TICK} minTickGap={40} tickLine={false} axisLine={{ stroke: C.hairline }} />
              <YAxis domain={tempAxis.domain} ticks={tempAxis.ticks} tick={AXIS_TICK} unit="°F" width={56} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip {...TOOLTIP} />
              <Line type="monotone" dataKey="temperature" stroke={C.text} strokeWidth={2} dot={false} name="Temperature °F" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Axis breakdown — only when the sensor reports per-axis peaks. */}
      {!loadingReadings && hasAxes && (
        <div style={CARD}>
          <h2 style={H2}>Peak acceleration by axis</h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 5, right: 8, bottom: 5, left: 0 }}>
              <CartesianGrid stroke={C.hairline} vertical={false} />
              <XAxis dataKey="time" tick={AXIS_TICK} minTickGap={40} tickLine={false} axisLine={{ stroke: C.hairline }} />
              <YAxis tick={AXIS_TICK} unit=" g" width={56} tickLine={false} axisLine={false} />
              <Tooltip {...TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12, fontFamily: F.mono }} />
              <Line type="monotone" dataKey="peakX" stroke={C.ink} strokeWidth={1.5} dot={false} name="X" />
              <Line type="monotone" dataKey="peakY" stroke={C.primary} strokeWidth={1.5} dot={false} name="Y" />
              <Line type="monotone" dataKey="peakZ" stroke={C.textMuted} strokeWidth={1.5} dot={false} name="Z" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Analysis — built from the same grade, action sentence and readings as
          the rest of this page, the Fleet card and the PDF. (It used to call
          /monitoring/motors/{id}/analysis: always the last 6 readings, and
          "flags" there are data-quality only — so a D motor read "No flags".) */}
      <div style={CARD}>
        <h2 style={H2}>Analysis</h2>
        {loadingReadings ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <div className="skeleton" style={{ height: 14, width: '75%', borderRadius: 4 }} />
            <div className="skeleton" style={{ height: 14, width: '100%', borderRadius: 4 }} />
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: offline ? C.textMuted : C.text }}>
            {analysisText({ grade, offline, lastTimestamp: motor.latestReading?.timestamp, velocity, temperature: cToF(motor.latestReading?.temperature),
              count: readings.length, periodLabel: PERIOD_WINDOW[period] })}
          </p>
        )}
      </div>
    </div>
  );
}

const PERIOD_WINDOW = { '24h': 'the last 24 hours', '7d': 'the last 7 days', '30d': 'the last 30 days' };

/** The Analysis card's sentences. Grade words match the badge; action matches the Fleet card and PDF. */
export function analysisText({ grade, offline, lastTimestamp, velocity, temperature, count, periodLabel }) {
  // Offline / no-data words are the Fleet card's, so the two never disagree.
  if (offline) return lastTimestamp ? `${offlineLabel(lastTimestamp)}.` : 'No reading yet.';
  if (!grade) return 'Waiting on the first reading.';
  const reading = [
    velocity != null ? `Vibration ${velocity.toFixed(2)} mm/s` : null,
    temperature != null ? `temperature ${temperature}°F` : null,
  ].filter(Boolean).join(', ');
  return [
    `Grade ${grade} · ${TILE_LABEL[tileForGrade(grade)]}. ${actionForGrade(grade)}`,
    reading ? `Latest: ${reading}.` : null,
    `Based on ${count} reading${count !== 1 ? 's' : ''} over ${periodLabel}.`,
  ].filter(Boolean).join(' ');
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ background: C.ground, border: `1px solid ${C.hairline}`, borderRadius: R.lg, padding: '12px 14px', minWidth: 0 }}>
      <div style={{ ...MONO, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>{label}</div>
      <div className="bs-metric-value" style={{ ...MONO, fontSize: 20, fontWeight: 500, color: color || C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
    </div>
  );
}
